/**
 * Re-prices platform trade-log rows from the broker's own fills.
 *
 * The backlog ledger stores the price we asked for (it falls back to the limit
 * price when Tradier has not reported a fill yet) and records zero commission,
 * so its P&L runs above the account. Tradier's history has the executed price
 * and the net cash amount (after per-contract fees) for every leg, but only a
 * trade date — no time — so rows are paired leg-by-leg on symbol, date,
 * direction and quantity, using the logged spread price to pick between
 * same-day rounds on the same strikes.
 */

const round2 = (n) => Math.round(n * 100) / 100;

const etDateFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/New_York',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const toDate = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const marketDateOf = (value) => {
  const d = toDate(value);
  return d ? etDateFormatter.format(d) : null;
};

const addDays = (iso, days) => {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

const sideSign = (side) => (String(side || '').toLowerCase().startsWith('sell') ? -1 : 1);

/** Legs opened by the entry order, as { symbol, qty, sign } with sign +1 = bought. */
export function legsOfBacklog(raw) {
  const option = raw?.entry?.option;
  if (!option) return [];
  if (Array.isArray(option.legs) && option.legs.length) {
    const legs = option.legs
      .filter((l) => l?.optionSymbol)
      .map((l) => ({ symbol: l.optionSymbol, qty: Math.abs(Number(l.quantity) || 0), sign: sideSign(l.side) }));
    // Older rows store leg quantities as the spread ratio (1/1) and the
    // contracts across all legs in entry.quantity (e.g. 6 = 3 spreads).
    const legTotal = legs.reduce((s, l) => s + l.qty, 0);
    const total = Math.abs(Number(raw.entry.quantity) || 0);
    if (legTotal > 0 && total > legTotal && total % legTotal === 0) {
      const scale = total / legTotal;
      return legs.map((l) => ({ ...l, qty: l.qty * scale }));
    }
    return legs;
  }
  if (option.optionSymbol) {
    return [{
      symbol: option.optionSymbol,
      qty: Math.abs(Number(raw.entry.quantity) || 1),
      sign: sideSign(option.side),
    }];
  }
  return [];
}

const MAX_COMBOS = 400;

/**
 * For each leg, candidate fill sets: single events of the exact size, else the
 * unused same-day events accumulated until the leg size (partial fills).
 */
function legCandidates(pool, leg, dates, wantSign) {
  for (const date of dates) {
    const same = (pool.get(`${leg.symbol}|${date}`) || []).filter(
      (e) => !e.used && e.category === 'trade' && Math.sign(e.quantity) === wantSign
    );
    if (!same.length) continue;
    const exact = same.filter((e) => Math.abs(e.quantity) === leg.qty).map((e) => [e]);
    if (exact.length) return exact;
    const group = [];
    let filled = 0;
    for (const e of same) {
      if (filled >= leg.qty) break;
      group.push(e);
      filled += Math.abs(e.quantity);
    }
    if (filled === leg.qty) return [group];
  }
  return [];
}

const avgPrice = (events) => {
  const qty = events.reduce((s, e) => s + Math.abs(e.quantity), 0);
  return qty ? events.reduce((s, e) => s + Math.abs(e.quantity) * (e.price || 0), 0) / qty : 0;
};

function pickFills(pool, legs, dates, dir, targetPrice) {
  const perLeg = legs.map((leg) => legCandidates(pool, leg, dates, leg.sign * dir));
  if (perLeg.some((c) => c.length === 0)) return null;

  let best = null;
  let combos = 0;
  const walk = (i, chosen) => {
    if (combos > MAX_COMBOS) return;
    if (i === legs.length) {
      combos += 1;
      const events = chosen.flat();
      if (new Set(events).size !== events.length) return;
      const net = legs.reduce((s, leg, k) => s + leg.sign * avgPrice(chosen[k]), 0);
      const score = targetPrice == null ? 0 : Math.abs(Math.abs(net) - Math.abs(targetPrice));
      if (!best || score < best.score) best = { score, events };
      return;
    }
    for (const c of perLeg[i]) walk(i + 1, [...chosen, c]);
  };
  walk(0, []);
  return best?.events || null;
}

function findExpirations(expirations, legs, fromDate) {
  const found = [];
  for (const leg of legs) {
    const e = (expirations.get(leg.symbol) || []).find((x) => !x.used && x.date >= fromDate);
    if (!e) return null;
    found.push(e);
  }
  return found;
}

/**
 * @param rows platform rows ({ id, raw }) — completed rows are re-priced.
 * @param tradierEvents normalized Tradier history events.
 * @param options.today ET market date (YYYY-MM-DD); defaults to now.
 * @returns {{ byId: Map<string, {status, tradierPnl, entryEvents, exitEvents}>, missingFills: object[] }}
 *   status: 'matched' | 'pending' (closed after Tradier's last posted session)
 *   | 'unmatched' (no fills on the account) | 'no_legs'
 */
export function reconcilePlatformTrades(rows, tradierEvents, options = {}) {
  const events = (tradierEvents || [])
    .filter((e) => (e.category === 'trade' || e.category === 'expiration') && e.symbol)
    .map((e) => ({ ...e, used: false }));

  const pool = new Map();
  const expirations = new Map();
  for (const e of events) {
    if (e.category === 'expiration') {
      if (!expirations.has(e.symbol)) expirations.set(e.symbol, []);
      expirations.get(e.symbol).push(e);
      continue;
    }
    const key = `${e.symbol}|${e.date}`;
    if (!pool.has(key)) pool.set(key, []);
    pool.get(key).push(e);
  }
  for (const list of expirations.values()) list.sort((a, b) => (a.date < b.date ? -1 : 1));

  const completed = rows
    .filter((r) => r.completed)
    .sort((a, b) => (toDate(a.raw?.entry?.filledAt)?.getTime() || 0) - (toDate(b.raw?.entry?.filledAt)?.getTime() || 0));

  // Tradier posts a session's fills the next day, so a row closed after the
  // last posted session can't be checked yet; any other unmatched row has no
  // fills on the account at all.
  const today = options.today || marketDateOf(new Date());
  const lastPosted = events.reduce((max, e) => (e.category === 'trade' && e.date > max ? e.date : max), '');
  const unmatched = (exitDate) => ({
    status: exitDate && (exitDate > lastPosted || exitDate >= today) ? 'pending' : 'unmatched',
    tradierPnl: null,
    entryEvents: [],
    exitEvents: [],
  });

  const byId = new Map();
  for (const row of completed) {
    const raw = row.raw || {};
    const legs = legsOfBacklog(raw);
    if (!legs.length || legs.some((l) => !l.qty)) {
      byId.set(row.id, { status: 'no_legs', tradierPnl: null, entryEvents: [], exitEvents: [] });
      continue;
    }
    const entryDate = marketDateOf(raw.entry?.filledAt || raw.createdAt);
    const exitDate = marketDateOf(raw.exit?.filledAt) || entryDate;

    const entryEvents = entryDate ? pickFills(pool, legs, [entryDate], 1, raw.entry?.price) : null;
    if (!entryEvents) {
      byId.set(row.id, unmatched(exitDate));
      continue;
    }
    entryEvents.forEach((e) => { e.used = true; });

    // The monitor can stamp the exit when it notices the close, not when it filled.
    const exitDates = [];
    for (let d = exitDate; d >= entryDate && exitDates.length < 10; d = addDays(d, -1)) exitDates.push(d);
    for (let k = 1; k <= 3; k += 1) exitDates.push(addDays(exitDate, k));

    let exitEvents = pickFills(pool, legs, exitDates, -1, raw.exit?.price);
    if (!exitEvents) exitEvents = findExpirations(expirations, legs, entryDate);
    if (!exitEvents) {
      entryEvents.forEach((e) => { e.used = false; });
      byId.set(row.id, unmatched(exitDate));
      continue;
    }
    exitEvents.forEach((e) => { e.used = true; });

    const tradierPnl = round2([...entryEvents, ...exitEvents].reduce((s, e) => s + e.amount, 0));
    byId.set(row.id, { status: 'matched', tradierPnl, entryEvents, exitEvents });
  }

  const missingFills = events.filter((e) => e.category === 'trade' && !e.used);
  return { byId, missingFills };
}
